export const ROLES = {
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin",
};

export const ALL_ADMIN_ROLES = [ROLES.ADMIN, ROLES.SUPER_ADMIN];

const normalizeFlag = (value) =>
  value === true || value === 1 || value === "1";

export const normalizeRole = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (normalized.includes("super")) {
    return ROLES.SUPER_ADMIN;
  }

  if (normalized === "administrator" || normalized === "normal_admin") {
    return ROLES.ADMIN;
  }

  return normalized;
};

const normalizePermission = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const getUserRoles = (user) => {
  const roles = new Set();

  if (!user) {
    return [];
  }

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      const resolved = normalizeRole(role?.name || role?.value || role);
      if (resolved) {
        roles.add(resolved);
      }
    });
  }

  [user.role, user.user_role, user.role_name].forEach((role) => {
    const resolved = normalizeRole(role);
    if (resolved) {
      roles.add(resolved);
    }
  });

  if (
    normalizeFlag(user.is_super_admin) ||
    normalizeFlag(user.isSuperAdmin) ||
    normalizeFlag(user.super_admin)
  ) {
    roles.add(ROLES.SUPER_ADMIN);
  }

  if (roles.size === 0 && user) {
    roles.add(ROLES.ADMIN);
  }

  return Array.from(roles);
};

export const getUserPermissions = (user) => {
  if (!user) {
    return [];
  }

  const permissions = new Set();
  const candidates = [user.permissions_list, user.permissions];

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      if (Array.isArray(role?.permissions)) {
        candidates.push(role.permissions);
      }
    });
  }

  candidates.forEach((list) => {
    if (!Array.isArray(list)) {
      return;
    }

    list.forEach((permission) => {
      const resolved = permission?.name || permission?.value || permission;
      if (resolved) {
        permissions.add(normalizePermission(resolved));
      }
    });
  });

  return Array.from(permissions);
};

export const getPrimaryRoleLabel = (user) => {
  const roles = getUserRoles(user);

  if (roles.includes(ROLES.SUPER_ADMIN)) {
    return "Super Admin";
  }

  if (roles.includes(ROLES.ADMIN)) {
    return "Admin";
  }

  return "Admin User";
};

export const canAccess = (user, accessConfig = {}) => {
  const { allowedRoles = [], requiredPermissions = [] } = accessConfig;
  const roles = getUserRoles(user);
  const permissions = getUserPermissions(user);

  if (
    allowedRoles.length > 0 &&
    !allowedRoles.some((role) => roles.includes(normalizeRole(role)))
  ) {
    return false;
  }

  if (
    requiredPermissions.length > 0 &&
    !requiredPermissions.every((permission) =>
      permissions.includes(normalizePermission(permission)),
    )
  ) {
    return false;
  }

  return true;
};

export const routeAccess = {
  dashboard: { allowedRoles: ALL_ADMIN_ROLES },
  courses: { allowedRoles: ALL_ADMIN_ROLES },
  courseEdit: { allowedRoles: [ROLES.SUPER_ADMIN] },
  courseView: { allowedRoles: ALL_ADMIN_ROLES },
  quiz: { allowedRoles: ALL_ADMIN_ROLES },
  quizSet: { allowedRoles: [ROLES.SUPER_ADMIN] },
  quizView: { allowedRoles: ALL_ADMIN_ROLES },
  analytics: { allowedRoles: ALL_ADMIN_ROLES },
  userManagement: { allowedRoles: [ROLES.SUPER_ADMIN] },
  learnerDetail: { allowedRoles: [ROLES.SUPER_ADMIN] },
  adminDetail: { allowedRoles: [ROLES.SUPER_ADMIN] },
  content: { allowedRoles: [ROLES.SUPER_ADMIN] },
  settings: { allowedRoles: [ROLES.SUPER_ADMIN] },
  rolesManager: { allowedRoles: [ROLES.SUPER_ADMIN] },
  createCourse: { allowedRoles: [ROLES.SUPER_ADMIN] },
  feedback: { allowedRoles: [ROLES.SUPER_ADMIN] },
  forum: { allowedRoles: ALL_ADMIN_ROLES },
  monitor: { allowedRoles: ALL_ADMIN_ROLES },
  certificates: { allowedRoles: ALL_ADMIN_ROLES },
  blog: {},
  askVik: { allowedRoles: ALL_ADMIN_ROLES },
  accessDenied: { allowedRoles: ALL_ADMIN_ROLES },
};